/**
 * SearchableSelect Hooks - VRP System v4
 * Enhanced mobile UX and design system optimizations following LEVER principles
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react'

// Enhanced debounced search hook with mobile optimizations
export const useDebouncedSearch = (value: string, delay: number = 300) => {
  const [debouncedValue, setDebouncedValue] = useState(value)

  useEffect(() => {
    // Shorter delay on mobile for better responsiveness
    const isMobile = window.innerWidth < 768
    const actualDelay = isMobile ? Math.max(150, delay * 0.5) : delay

    const handler = setTimeout(() => setDebouncedValue(value), actualDelay)
    return () => clearTimeout(handler)
  }, [value, delay])

  return debouncedValue
}

// Enhanced search cache hook with memory management for mobile
export const useSearchCache = <T>(cacheSize: number = 50) => {
  const cacheRef = useRef(new Map<string, T[]>())
  const lastAccessRef = useRef(new Map<string, number>())

  const setCache = useCallback(
    (key: string, results: T[]) => {
      const cache = cacheRef.current
      const lastAccess = lastAccessRef.current

      if (cache.size >= cacheSize) {
        // Remove least recently used entry
        let oldestKey = ''
        let oldestTime = Date.now()

        for (const [cacheKey, time] of lastAccess.entries()) {
          if (time < oldestTime) {
            oldestTime = time
            oldestKey = cacheKey
          }
        }

        if (oldestKey) {
          cache.delete(oldestKey)
          lastAccess.delete(oldestKey)
        }
      }

      cache.set(key, results)
      lastAccess.set(key, Date.now())
    },
    [cacheSize]
  )

  const getCache = useCallback((key: string) => {
    const result = cacheRef.current.get(key)
    if (result) {
      lastAccessRef.current.set(key, Date.now())
    }
    return result
  }, [])

  const clearCache = useCallback(() => {
    cacheRef.current.clear()
    lastAccessRef.current.clear()
  }, [])

  return { setCache, getCache, clearCache }
}

// Enhanced responsive hook with device capability detection
export const useResponsiveDropdown = () => {
  const [deviceInfo, setDeviceInfo] = useState({
    isMobile: false,
    isTouch: false,
    hasHover: false,
    screenSize: 'desktop' as 'mobile' | 'tablet' | 'desktop',
    preferReducedMotion: false,
  })

  useEffect(() => {
    const updateDeviceInfo = () => {
      const width = window.innerWidth
      const isMobile = width < 768
      const isTablet = width >= 768 && width < 1024
      const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0
      const hasHover = window.matchMedia('(hover: hover)').matches
      const preferReducedMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches

      setDeviceInfo({
        isMobile,
        isTouch,
        hasHover,
        screenSize: isMobile ? 'mobile' : isTablet ? 'tablet' : 'desktop',
        preferReducedMotion,
      })
    }

    updateDeviceInfo()
    window.addEventListener('resize', updateDeviceInfo)

    return () => window.removeEventListener('resize', updateDeviceInfo)
  }, [])

  return deviceInfo
}

// Touch gesture hook for swipe-to-dismiss functionality
export const useSwipeGestures = (
  onSwipeDown?: () => void,
  onSwipeUp?: () => void,
  threshold = 50
) => {
  const startY = useRef<number | null>(null)
  const [isSwaping, setIsSwaping] = useState(false)

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    startY.current = e.touches[0].clientY
    setIsSwaping(false)
  }, [])

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (startY.current === null) return

    const currentY = e.touches[0].clientY
    const diffY = startY.current - currentY

    // Detect swipe direction
    if (Math.abs(diffY) > 10) {
      setIsSwaping(true)
    }
  }, [])

  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (startY.current === null) return

      const endY = e.changedTouches[0].clientY
      const diffY = startY.current - endY

      if (Math.abs(diffY) > threshold) {
        if (diffY > 0) {
          // Swiped up
          onSwipeUp?.()
        } else {
          // Swiped down
          onSwipeDown?.()
        }
      }

      startY.current = null
      setIsSwaping(false)
    },
    [onSwipeDown, onSwipeUp, threshold]
  )

  return {
    touchHandlers: {
      onTouchStart: handleTouchStart,
      onTouchMove: handleTouchMove,
      onTouchEnd: handleTouchEnd,
    },
    isSwaping,
  }
}

// Enhanced keyboard navigation for mobile
export const useKeyboardNavigation = (
  isOpen: boolean,
  onClose: () => void,
  itemCount: number
) => {
  const [selectedIndex, setSelectedIndex] = useState(-1)

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!isOpen) return

      // Check if the user is typing in the search input - don't intercept space key
      const target = e.target as HTMLElement
      const isTypingInInput =
        target.tagName === 'INPUT' ||
        target.getAttribute('data-slot') === 'command-input'

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          setSelectedIndex(prev => (prev + 1) % itemCount)
          break
        case 'ArrowUp':
          e.preventDefault()
          setSelectedIndex(prev => (prev - 1 + itemCount) % itemCount)
          break
        case 'Enter':
          e.preventDefault()
          // Handle selection - will be managed by parent component
          break
        case ' ':
          // Only prevent space if user is NOT typing in search input
          if (!isTypingInInput) {
            e.preventDefault()
            // Handle selection - will be managed by parent component
          }
          break
        case 'Escape':
          e.preventDefault()
          onClose()
          break
        case 'Tab':
          // Allow natural tab behavior on mobile
          if (window.innerWidth < 768) {
            e.preventDefault()
            onClose()
          }
          break
      }
    },
    [isOpen, onClose, itemCount]
  )

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown)
      return () => document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, handleKeyDown])

  return {
    selectedIndex,
    setSelectedIndex,
    resetSelection: () => setSelectedIndex(-1),
  }
}

// Performance monitoring hook for mobile optimization
export const usePerformanceOptimization = () => {
  const [isLowEndDevice, setIsLowEndDevice] = useState(false)
  const frameTimeRef = useRef<number>(0)
  const frameCountRef = useRef<number>(0)

  useEffect(() => {
    // Detect low-end devices based on various factors
    const checkDeviceCapabilities = () => {
      // Memory-based detection
      const memory = (navigator as any).deviceMemory || 4
      const hardwareConcurrency = navigator.hardwareConcurrency || 4

      // Connection-based detection
      const connection = (navigator as any).connection
      const effectiveType = connection?.effectiveType || '4g'

      // Simple heuristic for low-end device detection
      const isLowEnd =
        memory < 4 ||
        hardwareConcurrency < 4 ||
        ['slow-2g', '2g', '3g'].includes(effectiveType)

      setIsLowEndDevice(isLowEnd)
    }

    checkDeviceCapabilities()

    // Monitor frame rate on mobile
    const isMobile = window.innerWidth < 768
    if (isMobile) {
      let lastTime = performance.now()

      const measureFrameTime = (currentTime: number) => {
        const delta = currentTime - lastTime
        frameTimeRef.current += delta
        frameCountRef.current++

        if (frameCountRef.current >= 60) {
          const avgFrameTime = frameTimeRef.current / frameCountRef.current
          // If average frame time is > 20ms, consider it a low-end device
          if (avgFrameTime > 20) {
            setIsLowEndDevice(true)
          }
          frameTimeRef.current = 0
          frameCountRef.current = 0
        }

        lastTime = currentTime
        requestAnimationFrame(measureFrameTime)
      }

      const animationId = requestAnimationFrame(measureFrameTime)
      return () => cancelAnimationFrame(animationId)
    }
  }, [])

  return {
    isLowEndDevice,
    shouldReduceAnimations: isLowEndDevice,
    shouldUseVirtualization: isLowEndDevice,
  }
}

// Loading state hook optimized for mobile
export const useMobileOptimizedLoading = (isLoading: boolean) => {
  const [showLoader, setShowLoader] = useState(false)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    if (isLoading) {
      // Show loader after a short delay on mobile to prevent flashing
      const isMobile = window.innerWidth < 768
      const delay = isMobile ? 100 : 0

      timeoutRef.current = setTimeout(() => {
        setShowLoader(true)
      }, delay)
    } else {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      setShowLoader(false)
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [isLoading])

  return showLoader
}
